import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Role-based permissions mapping (mirrors backend /api/middleware/rbac.php)
const ROLE_PERMISSIONS: Record<string, string[]> = {
  // ADMIN: Full system access - all features
  admin: [
    'instances.view', 'instances.create', 'instances.manage', 'instances.delete',
    'instances.start', 'instances.stop', 'instances.restart', 'instances.logs',
    'users.view', 'users.create', 'users.manage', 'users.delete',
    'themes.view', 'themes.create', 'themes.manage', 'themes.delete', 'themes.export',
    'dsl.view', 'dsl.execute', 'dsl.manage',
    'system.config', 'system.cache', 'system.cache.clear', 'system.cache.manage',
    'system.security', 'system.logs', 'system.backup',
    'cores.view', 'cores.manage', 'cores.update',
    'tenants.view', 'tenants.create', 'tenants.manage', 'tenants.delete',
  ],
  // MANAGER: Instance on/off, cache management, core updates
  // NO: user management, system config, tenant management, delete operations
  manager: [
    'instances.view', 'instances.start', 'instances.stop', 'instances.restart', 'instances.logs',
    'system.cache', 'system.cache.clear', 'system.cache.manage',
    'cores.view', 'cores.update',
  ],
  // VIEWER: Read-only access to instances only
  // NO: themes, DSL, system, cores, users, tenants
  viewer: [
    'instances.view',
  ],
};

interface User {
  id: number;
  username: string;
  full_name?: string;
  email?: string;
  role: 'admin' | 'manager' | 'viewer';
  permissions?: string[];
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  login: (username: string, password: string, token?: string, userData?: User) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (roles: string | string[]) => boolean;
  isAdmin: boolean;
  isManager: boolean;
  isViewer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
      localStorage.setItem('last_activity', Date.now().toString());
    };

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, []);

  // Check for session timeout
  useEffect(() => {
    if (!token) return;

    const checkTimeout = () => {
      const storedActivity = localStorage.getItem('last_activity');
      const lastActivityTime = storedActivity ? parseInt(storedActivity) : lastActivity;
      
      if (Date.now() - lastActivityTime > SESSION_TIMEOUT) {
        // Session expired - logout
        console.log('Session expired due to inactivity');
        logout();
        window.location.href = '/login?expired=1';
      }
    };

    // Check every minute
    const interval = setInterval(checkTimeout, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [token, lastActivity]);

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = async (username: string, password: string, providedToken?: string, userData?: User): Promise<boolean> => {
    try {
      // If token and user data provided (from 2FA verification), use them directly
      if (providedToken && userData) {
        setToken(providedToken);
        setUser(userData);
        localStorage.setItem('auth_token', providedToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        return true;
      }
      
      // Otherwise, do normal login (this path is no longer used with 2FA)
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success && data.token) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  };

  // Update user data without page reload
  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((prevUser: User | null) => {
      if (!prevUser) return prevUser;
      const updatedUser = { ...prevUser, ...userData };
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    
    const role = user.role;
    
    // Admin has all permissions
    if (role === 'admin' || role === 'administrator') {
      return true;
    }
    
    // Check role-based permissions
    const rolePermissions = ROLE_PERMISSIONS[role] || [];
    if (rolePermissions.includes(permission)) {
      return true;
    }
    
    // Check custom permissions
    if (user.permissions?.includes(permission)) {
      return true;
    }
    
    return false;
  }, [user]);

  // Check if user has one of the specified roles
  const hasRole = useCallback((roles: string | string[]): boolean => {
    if (!user) return false;
    
    const roleArray = Array.isArray(roles) ? roles : [roles];
    const userRole = user.role;
    
    // Admin always passes role checks
    if (userRole === 'admin' || userRole === 'administrator') {
      return true;
    }
    
    return roleArray.includes(userRole);
  }, [user]);

  // Role convenience flags
  const isAdmin = user?.role === 'admin' || user?.role === 'administrator';
  const isManager = isAdmin || user?.role === 'manager';
  const isViewer = isManager || user?.role === 'viewer';

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        logout,
        updateUser,
        isAuthenticated: !!token,
        hasPermission,
        hasRole,
        isAdmin,
        isManager,
        isViewer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
